import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { 
  BarChart3, 
  TrendingUp,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Banknote,
  LogOut,
  ArrowLeft,
  Brain,
  Loader2,
  Building2,
  Users
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const AnalyticsDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [disbursements, setDisbursements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, decisionsRes, disbursementsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/decisions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/disbursements`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAnalytics(analyticsRes.data);
      setDecisions(decisionsRes.data);
      setDisbursements(disbursementsRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="analytics-dashboard">
      {/* Header */}
      <header className="bg-[#0A2540] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Tzur Finances" className="h-10 brightness-0 invert" />
              <Badge variant="secondary" className="bg-amber-500 text-[#0A2540] hover:bg-amber-500">
                Analytics Dashboard
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/officer/dashboard">
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <Link to="/officer/credit-decisioning">
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                  <Brain className="h-4 w-4 mr-2" />
                  Credit Decisioning
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540] flex items-center gap-3">
            <BarChart3 className="h-8 w-8" />
            Analytics Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Applications, decisions, and disbursements overview</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-purple-600 to-purple-800 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-200 text-sm">Total Registrations</p>
                  <p className="text-3xl font-bold font-mono mt-1">
                    {analytics?.registrations?.total || 0}
                  </p>
                  <p className="text-xs text-purple-300 mt-1">
                    {analytics?.registrations?.customers || 0} customers • {analytics?.registrations?.loan_officers || 0} officers
                  </p>
                </div>
                <Users className="h-10 w-10 text-white/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#0A2540] to-[#1e3a8a] text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-300 text-sm">Total Applications</p>
                  <p className="text-3xl font-bold font-mono mt-1">
                    {analytics?.applications?.total || 0}
                  </p>
                </div>
                <FileText className="h-10 w-10 text-white/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Amount Requested</p>
                  <p className="text-2xl font-bold font-mono text-[#0A2540] mt-1">
                    {formatCurrency(analytics?.amounts?.total_requested)}
                  </p>
                </div>
                <DollarSign className="h-10 w-10 text-gray-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Amount Approved</p>
                  <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">
                    {formatCurrency(analytics?.amounts?.total_approved)}
                  </p>
                </div>
                <CheckCircle className="h-10 w-10 text-emerald-100" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Amount Disbursed</p>
                  <p className="text-2xl font-bold font-mono text-amber-600 mt-1">
                    {formatCurrency(analytics?.amounts?.total_disbursed)}
                  </p>
                </div>
                <Banknote className="h-10 w-10 text-amber-100" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different analytics views */}
        <Tabs defaultValue="applications" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="applications">Applications</TabsTrigger>
            <TabsTrigger value="decisions">Decisions</TabsTrigger>
            <TabsTrigger value="disbursements">Disbursements</TabsTrigger>
            <TabsTrigger value="credit">Credit Scores</TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Application Status Breakdown */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Application Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span>Pending</span>
                    </div>
                    <span className="font-mono font-semibold">{analytics?.applications?.pending || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <span>Approved</span>
                    </div>
                    <span className="font-mono font-semibold">{analytics?.applications?.approved || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>Rejected</span>
                    </div>
                    <span className="font-mono font-semibold">{analytics?.applications?.rejected || 0}</span>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Approval Rate</span>
                      <span className="font-mono font-bold text-emerald-600">
                        {analytics?.applications?.approval_rate || 0}%
                      </span>
                    </div>
                    <Progress value={analytics?.applications?.approval_rate || 0} className="mt-2 h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* By Loan Type */}
              <Card className="bg-white lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Applications by Loan Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Loan Type</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Total Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics?.by_loan_type?.map((type, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{type._id || 'Unknown'}</TableCell>
                          <TableCell className="text-right font-mono">{type.count}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(type.total_amount)}</TableCell>
                        </TableRow>
                      ))}
                      {(!analytics?.by_loan_type || analytics.by_loan_type.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-gray-500">No data available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            {/* Recent Applications */}
            <Card className="bg-white mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Recent Applications</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Business</TableHead>
                      <TableHead>Loan Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics?.recent_applications?.map((app, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-sm">{app.application_id?.slice(0, 12)}...</TableCell>
                        <TableCell>{app.business_name}</TableCell>
                        <TableCell>{app.loan_type}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(app.amount)}</TableCell>
                        <TableCell>
                          <Badge variant={app.status === 'approved' ? 'default' : app.status === 'rejected' ? 'destructive' : 'secondary'}>
                            {app.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(app.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Decisions Tab */}
          <TabsContent value="decisions">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Decision Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {decisions?.by_status?.map((status, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium capitalize">{status._id || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">Avg: {formatCurrency(status.avg_amount)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold">{status.count}</p>
                          <p className="text-sm text-gray-500">{formatCurrency(status.total_amount)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Processing Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-[#0A2540] mx-auto mb-4" />
                    <p className="text-sm text-gray-500">Average Processing Time</p>
                    <p className="text-4xl font-bold font-mono text-[#0A2540] mt-2">
                      {decisions?.average_processing_time_hours || 0}
                    </p>
                    <p className="text-gray-500">hours</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Disbursements Tab */}
          <TabsContent value="disbursements">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Disbursement Overview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Total Disbursed</span>
                    <span className="font-mono font-bold text-emerald-600">
                      {formatCurrency(disbursements?.total?.amount)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Number of Disbursements</span>
                    <span className="font-mono font-bold">{disbursements?.total?.count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Average Disbursement</span>
                    <span className="font-mono font-bold">{formatCurrency(disbursements?.total?.average)}</span>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between text-amber-600">
                      <span className="font-medium">Pending Disbursement</span>
                      <span className="font-mono font-bold">{formatCurrency(disbursements?.pending?.amount)}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{disbursements?.pending?.count || 0} loans awaiting disbursement</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Disbursements by Bank</CardTitle>
                </CardHeader>
                <CardContent>
                  {disbursements?.by_bank?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Bank</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                          <TableHead className="text-right">Total Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {disbursements.by_bank.map((bank, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{bank._id || 'Unknown'}</TableCell>
                            <TableCell className="text-right font-mono">{bank.count}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(bank.total_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Banknote className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No disbursements recorded yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Disbursements */}
            <Card className="bg-white mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Recent Disbursements</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.recent_disbursements?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reference</TableHead>
                        <TableHead>Business</TableHead>
                        <TableHead>Bank</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.recent_disbursements.map((disb, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-mono text-sm">{disb.reference_number}</TableCell>
                          <TableCell>{disb.business_name}</TableCell>
                          <TableCell>{disb.bank_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(disb.amount)}</TableCell>
                          <TableCell>{formatDate(disb.disbursement_date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No disbursements recorded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Credit Scores Tab */}
          <TabsContent value="credit">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Credit Score Statistics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <Brain className="h-12 w-12 text-[#0A2540] mx-auto mb-4" />
                    <p className="text-sm text-gray-500">Average Credit Score</p>
                    <p className="text-4xl font-bold font-mono text-[#0A2540] mt-2">
                      {analytics?.credit_scores?.average || 0}
                    </p>
                    <p className="text-gray-500">out of 100</p>
                  </div>
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Highest Score</span>
                      <span className="font-mono font-bold text-emerald-600">{analytics?.credit_scores?.max || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Lowest Score</span>
                      <span className="font-mono font-bold text-red-600">{analytics?.credit_scores?.min || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Applications Assessed</span>
                      <span className="font-mono font-bold">{analytics?.credit_scores?.assessed_count || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Credit Scoring Model</CardTitle>
                  <CardDescription>Factors used in credit assessment</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-[#0A2540]">Years in Business</p>
                      <p className="text-sm text-gray-500">Up to +15 points</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-[#0A2540]">Debt-to-Revenue Ratio</p>
                      <p className="text-sm text-gray-500">Up to +15 points</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-[#0A2540]">Loan-to-Revenue Ratio</p>
                      <p className="text-sm text-gray-500">Up to +10 points</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-[#0A2540]">Cash Flow Coverage</p>
                      <p className="text-sm text-gray-500">Up to +10 points</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-[#0A2540]">Business Size</p>
                      <p className="text-sm text-gray-500">Up to +5 points</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-[#0A2540]">Collateral/Security</p>
                      <p className="text-sm text-gray-500">Up to +10 points</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg md:col-span-2">
                      <p className="font-medium text-[#0A2540]">Alternative Data</p>
                      <p className="text-sm text-gray-500">Up to +15 points (based on data sources provided)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
